import { FC, useEffect, useMemo, useState } from 'react';

import { useFormikContext } from 'formik';
import { Input } from 'ui/components/form';
import { Button } from 'ui/components/common';
import { cn } from 'utils/helpers';
import useModal from 'ui/partials/Modal/useModal';
import { BiCaretDown } from 'react-icons/bi';
import { Combobox, Popover, Transition } from '@headlessui/react';
import { HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import { debounce } from 'lodash';

import { TASK_ASSOCIATE_DEAL_FORM } from './schema';
import {
  useGetDeal,
  useGetDeals,
  useGetPipeline,
  useGetPipelines,
} from '../hooks';

type Props = {
  isLoading: boolean;
};

const TaskAssociateDealForm: FC<Props> = () => {
  const { hideModal } = useModal();

  const { values, isSubmitting, handleSubmit, setFieldValue } =
    useFormikContext<TASK_ASSOCIATE_DEAL_FORM>();

  const [pipelineKeyword, setPipelineKeyword] = useState('');
  const [dealKeyword, setDealKeyword] = useState('');
  const { data: pipelines } = useGetPipelines({ keyword: pipelineKeyword });
  const { data: pipeline } = useGetPipeline(values.pipelineId as string);

  const { data: deals } = useGetDeals(
    {
      stageId: values.stageId,
      pipelineId: values.pipelineId,
    },
    !!values.stageId && !!values.pipelineId,
  );

  const { data: dealsSearch } = useGetDeals({
    keyword: dealKeyword,
    pipelineId: values.pipelineId,
  });

  const { data: deal } = useGetDeal(values.dealId);

  const pipelineOptions = useMemo(
    () =>
      pipelines?.map((pipeline) => ({
        value: pipeline?._id,
        label: pipeline.name,
      })) ?? [],
    [pipelines],
  );

  const stageOptions = useMemo(
    () =>
      pipeline?.stage?.map?.((_stage) => ({
        value: _stage?._id,
        label: _stage.name,
      })) ?? [],
    [pipeline?.stage],
  );

  const dealOptions = useMemo(
    () =>
      deals?.data?.map((deal) => ({
        value: deal?._id,
        label: deal.name,
      })) ?? [],
    [deals],
  );

  const dealSearchOptions = useMemo(
    () =>
      dealsSearch?.data?.map((deal) => ({
        value: deal?._id,
        label: deal.name,
      })) ?? [],
    [dealsSearch?.data],
  );

  useEffect(() => {
    if (!values.pipelineId) {
      if (pipelineOptions.length) {
        setFieldValue('pipelineId', pipelineOptions[0].value);
      }
    }
  }, [pipelineOptions, setFieldValue, values.pipelineId]);

  useEffect(() => {
    if (deal) {
      setFieldValue('stageId', deal.stageId);
    }
  }, [deal, setFieldValue]);

  return (
    <form
      onSubmit={(e) => {
        handleSubmit(e);
      }}
    >
      <div>
        <Popover className={'mb-[10px] relative'}>
          {({ close, open }) => (
            <>
              <Popover.Button
                className={'flex gap-2 items-center text-base font-semibold'}
              >
                <div>{pipeline?.name}</div>
                <BiCaretDown className="w-4 h-4" />
              </Popover.Button>
              <Transition
                show={open}
                enter="transition-opacity duration-75"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Popover.Panel
                  className={cn(
                    'top-full',
                    'left-0',
                    'absolute z-10',
                    'mt-1',
                    'rounded-md',
                    'py-[14px]',
                    'w-full',
                    'border bg-fill-tertiary',
                    'border-separator-secondary',
                    'shadow-leapify-md',
                  )}
                >
                  <div className="px-4">
                    <Input
                      placeholder="Search Pipeline"
                      className="w-full"
                      icon={
                        <HiOutlineMagnifyingGlass className="h-6 w-6 text-content" />
                      }
                      onChange={debounce(
                        (e) => setPipelineKeyword(e.target.value),
                        500,
                      )}
                    />
                  </div>
                  <div
                    className={cn(
                      'gap-y-[18px]',
                      'mt-[18px]',
                      'flex',
                      'flex-col',
                      'items-start',
                      'max-h-[290px]',
                      'overflow-y-auto',
                    )}
                  >
                    {pipelineOptions.map((option) => (
                      <button
                        type="button"
                        key={option.value}
                        onClick={() => {
                          setFieldValue('pipelineId', option.value);
                          setPipelineKeyword('');
                          close();
                        }}
                        className={cn(
                          'w-full',
                          'text-left',
                          'px-4',
                          'transition',
                          'focus:bg-secondary-lightest',
                          'hover:bg-secondary-lightest',
                        )}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>

        <Combobox
          as={'div'}
          className={'relative'}
          value={dealKeyword}
          onChange={(value) => setFieldValue('dealId', value)}
        >
          <Combobox.Input
            as={Input}
            placeholder="Search deal"
            className="w-full"
            icon={<HiOutlineMagnifyingGlass className="h-6 w-6 text-content" />}
            onChange={debounce((e) => setDealKeyword(e.target.value), 500)}
          />
          <Combobox.Options
            className={cn(
              'w-full',
              'mt-[3px]',
              'rounded-md',
              'border border-separator-primary shadow-leapify-md',
              'py-[10px] ',
              'px-5 ',
              'space-y-[6px] ',
              'absolute ',
              'top-full ',
              'left-0 ',
              'bg-fill-tertiary',
              'max-h-[300px] overflow-auto',
            )}
          >
            {dealSearchOptions.map((deal) => (
              <Combobox.Option
                className={({ active }) =>
                  cn(
                    'cursor-pointer transition',
                    'py-2',
                    active ? 'font-semibold text-primary-base' : '',
                  )
                }
                key={deal.value}
                value={deal.value}
              >
                {deal.label}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Combobox>

        <div className="border-b border-b-separator-primary my-5 w-full"></div>

        <div className="flex gap-[100px]">
          <div className="flex-1 space-y-2">
            <div className="text-sm font-semibold">Stages</div>
            <div
              className={cn(
                'py-[10px] flex flex-col gap-2 ',
                'max-h-[300px] overflow-y-auto scrollbar-thin',
              )}
            >
              {stageOptions.map((stage) => (
                <button
                  type="button"
                  key={stage.value}
                  className={cn(
                    stage.value === values.stageId
                      ? 'border  border-primary-base font-semibold text-primary-base px-2'
                      : '',
                    'py-[3px] text-left text-xs rounded-md tracking-wider leading-5',
                  )}
                  onClick={() => {
                    setFieldValue('stageId', stage.value);
                  }}
                >
                  {stage.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 space-y-2">
            <div className="text-sm font-semibold">Deal</div>
            <div
              className={cn(
                'py-[10px] flex flex-col gap-2 ',
                'max-h-[300px] overflow-y-auto scrollbar-thin',
              )}
            >
              {dealOptions.map((deal) => (
                <button
                  type="button"
                  key={deal.value}
                  className={cn(
                    deal.value === values.dealId
                      ? 'font-semibold bg-[#F1F4F8] px-2'
                      : '',
                    'py-[3px] text-left text-xs rounded-md tracking-wider leading-5',
                  )}
                  onClick={() => {
                    setFieldValue('dealId', deal.value);
                  }}
                >
                  {deal.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div
        className={cn(
          'flex justify-end gap-3',
          'px-2.5 pt-5 mt-5',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          onClick={hideModal}
          label="CANCEL"
          size="sm"
          intent="tertiary"
          disabled={isSubmitting}
        />
        <Button
          type="submit"
          label="ASSOCIATE DEAL"
          size="sm"
          disabled={!deal}
        />
      </div>
    </form>
  );
};

export default TaskAssociateDealForm;
